import { html, TemplateResult, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { systemChevronLeft, systemCross } from '@dertour/ui-components/icons';

const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const GRID_SELECTOR = '.der-tm-roundtrip-gallery-overlay__grid';

/**
 * Generic overlay dialog shell.
 *
 * Renders `content` inside a full-viewport native <dialog>.
 *
 * Fires (MFE-intern):
 *   overlay-close – when the user closes the overlay (ESC or x button)
 *   overlay-back  – when the user clicks the back button
 *
 * Note: `gallery-close` is still emitted for backwards compatibility inside this MFE.
 */
@localized()
@customElement('der-tm-roundtrip-gallery-overlay')
export class GalleryOverlay extends LitElement {
    protected override createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Light DOM – render inside parent's Shadow DOM
    }

    /** Whether the overlay is open */
    @property({ type: Boolean, reflect: true })
    open = false;

    /**
     * Optional back button.
     *
     * When true, the overlay renders a back button and emits `overlay-back` on click.
     */
    @property({ type: Boolean, attribute: 'back-enabled' })
    backEnabled = false;

    /** Content to render inside the overlay */
    @property({ attribute: false })
    content: TemplateResult = html`${nothing}`;

    /** Whether fullscreen slider mode is active (disables modal content scrolling). */
    @property({ type: Boolean, attribute: 'fullscreen-mode' })
    fullscreenMode = false;

    /** Accessible label for the dialog */
    @property({ type: String, attribute: 'aria-label' })
    ariaLabel = msg('Overlay', { desc: 'Accessible label for a generic overlay dialog' });

    @query('dialog')
    private _dialog!: HTMLDialogElement;

    @query('.der-tm-roundtrip-gallery-overlay__back')
    private _backButton?: HTMLButtonElement;

    @query('.der-tm-roundtrip-gallery-overlay__close')
    private _closeButton?: HTMLButtonElement;

    @query('.der-tm-roundtrip-gallery-overlay__content-inner')
    private _contentInner?: HTMLElement;

    private _safariGridRaf?: number;
    private _historyPushed = false;
    private _handlingPopstate = false;
    private _focusTrapRedirecting = false;

    private _onPopState = (): void => {
        if (!this.open) return;
        this._handlingPopstate = true;
        this._historyPushed = false;
        this._close();
        this._handlingPopstate = false;
    };

    private _onDocumentKeyDown = (event: KeyboardEvent): void => {
        this._handleContentTabLoop(event);
    };

    private _onDocumentFocusIn = (event: FocusEvent): void => {
        if (!this.open || !this._dialog?.open || this._focusTrapRedirecting) return;

        const target = event.target as Node | null;
        if (target && this._dialog.contains(target)) return;

        const orderedFocusable = this._getOrderedFocusableElements();
        const fallbackTarget = orderedFocusable[0] ?? this._closeButton ?? this._dialog;
        if (!fallbackTarget) return;

        this._focusTrapRedirecting = true;
        queueMicrotask(() => {
            fallbackTarget.focus();
            this._focusTrapRedirecting = false;
        });
    };

    private _bindFocusTrapListeners(): void {
        document.addEventListener('keydown', this._onDocumentKeyDown, true);
        document.addEventListener('focusin', this._onDocumentFocusIn, true);
    }

    private _unbindFocusTrapListeners(): void {
        document.removeEventListener('keydown', this._onDocumentKeyDown, true);
        document.removeEventListener('focusin', this._onDocumentFocusIn, true);
    }

    /** Attach global listeners when component is connected. */
    override connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('popstate', this._onPopState);
    }

    /** Cleanup global listeners when component is disconnected. */
    override disconnectedCallback(): void {
        if (this._safariGridRaf !== undefined) {
            cancelAnimationFrame(this._safariGridRaf);
            this._safariGridRaf = undefined;
        }
        this._unbindFocusTrapListeners();
        window.removeEventListener('popstate', this._onPopState);
        super.disconnectedCallback();
    }

    private _isSafariBrowser(): boolean {
        const ua = navigator.userAgent;
        return /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Android|FxiOS|EdgiOS/i.test(ua);
    }

    private _getGridElement(): HTMLElement | null {
        return this._contentInner?.querySelector<HTMLElement>(GRID_SELECTOR) ?? null;
    }

    private _applySafariGridRows = (): void => {
        if (!this.open || !this._dialog?.open || !this._isSafariBrowser()) return;
        const grid = this._getGridElement();
        if (!grid) return;

        const styles = getComputedStyle(grid);
        const columns = styles.gridTemplateColumns.split(' ').filter((col) => col && col !== '0px').length;
        if (!columns) return;

        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        const paddingLeft = parseFloat(styles.paddingLeft || '0') || 0;
        const paddingRight = parseFloat(styles.paddingRight || '0') || 0;
        const contentWidth = grid.clientWidth - paddingLeft - paddingRight;
        if (contentWidth <= 0) return;

        const colWidth = (contentWidth - gap * (columns - 1)) / columns;
        const rowHeight = Math.max(1, (colWidth * 2) / 3);

        grid.style.setProperty('--safari-grid-row', `${rowHeight}px`);
        grid.style.gridAutoRows = 'var(--safari-grid-row)';

        const images = grid.querySelectorAll<HTMLImageElement>('img');
        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', this._scheduleSafariGridStabilization, { once: true });
            }
        });
    };

    private _scheduleSafariGridStabilization = (): void => {
        if (!this._isSafariBrowser()) return;
        if (this._safariGridRaf !== undefined) {
            cancelAnimationFrame(this._safariGridRaf);
        }

        let frames = 0;
        const step = () => {
            this._applySafariGridRows();
            frames += 1;
            if (this.open && frames < 8) {
                this._safariGridRaf = requestAnimationFrame(step);
            } else {
                this._safariGridRaf = undefined;
            }
        };

        this._safariGridRaf = requestAnimationFrame(step);
    };

    /**
     * Open / close the native dialog whenever `open` changes
     * @param changed - Map of property names that have changed
     */
    override updated(changed: Map<string, unknown>): void {
        if (changed.has('open')) {
            if (this.open) {
                this._bindFocusTrapListeners();
                if (!this._historyPushed) {
                    history.pushState({ ...(history.state ?? {}), derTmGalleryOverlayOpen: true }, '');
                    this._historyPushed = true;
                }
                // Lock body scroll when overlay opens
                document.body.style.overflow = 'hidden';
                document.body.style.overscrollBehavior = 'contain';
                this._dialog?.showModal();
                // Some browsers auto-focus an element inside <dialog> on open.
                // Clear that initial focus so the first explicit Tab lands on item #1.
                queueMicrotask(() => {
                    const active = this._getActiveElement() as HTMLElement | null;
                    if (active && this._dialog?.contains(active)) {
                        active.blur();
                    }
                    this._scheduleSafariGridStabilization();
                });
            } else {
                if (this._safariGridRaf !== undefined) {
                    cancelAnimationFrame(this._safariGridRaf);
                    this._safariGridRaf = undefined;
                }
                this._unbindFocusTrapListeners();
                // Restore body scroll when overlay closes
                document.body.style.overflow = '';
                document.body.style.overscrollBehavior = '';
                if (this._dialog?.open) this._dialog.close();

                if (this._historyPushed && !this._handlingPopstate) {
                    this._historyPushed = false;
                    history.back();
                } else {
                    this._historyPushed = false;
                }
            }
        }

        if (this.open && changed.has('content')) {
            this._scheduleSafariGridStabilization();
        }
    }

    /**
     * ESC key on the <dialog> fires 'cancel' – treat it as close
     * @param e - The cancel event
     */
    private _onCancel = (e: Event): void => {
        e.preventDefault();
        this._close();
    };

    private _onPointerDown = (e: PointerEvent): void => {
        if (!this.open) return;
        const rect = this._dialog?.getBoundingClientRect();
        if (!rect) return;
        const isOutside =
            e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom;
        if (isOutside) {
            this._close();
        }
    };

    private _close = (): void => {
        if (this._dialog?.open) {
            this._dialog.close();
        }
        this.open = false;
        this.dispatchEvent(new CustomEvent('overlay-close'));
        this.dispatchEvent(new CustomEvent('gallery-close'));
    };

    private _back = (): void => {
        this.dispatchEvent(new CustomEvent('overlay-back'));
    };

    private _getActiveElement(): Element | null {
        const root = this.getRootNode();
        if (root instanceof ShadowRoot) {
            return root.activeElement;
        }
        return document.activeElement;
    }

    private _getContentFocusableElements(): HTMLElement[] {
        if (!this._contentInner) return [];
        return Array.from(this._contentInner.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
            (element) =>
                !element.hasAttribute('disabled') &&
                !element.getAttribute('aria-hidden') &&
                element.tabIndex >= 0 &&
                element.getClientRects().length > 0
        );
    }

    private _getOrderedFocusableElements(): HTMLElement[] {
        const ordered: HTMLElement[] = [];
        ordered.push(...this._getContentFocusableElements());
        if (this._backButton && !this._backButton.disabled) ordered.push(this._backButton);
        if (this._closeButton && !this._closeButton.disabled) ordered.push(this._closeButton);
        return ordered;
    }

    private _handleContentTabLoop(event: KeyboardEvent): void {
        if (event.key !== 'Tab' || event.defaultPrevented || !this.open || !this._dialog?.open) return;

        const activeElement = this._getActiveElement() as HTMLElement | null;
        const orderedFocusable = this._getOrderedFocusableElements();
        if (!orderedFocusable.length) return;

        const activeIndex = activeElement ? orderedFocusable.indexOf(activeElement) : -1;

        if (activeIndex === -1) {
            event.preventDefault();
            const edgeElement = event.shiftKey ? orderedFocusable[orderedFocusable.length - 1] : orderedFocusable[0];
            edgeElement?.focus();
            return;
        }

        event.preventDefault();
        const nextIndex = event.shiftKey
            ? (activeIndex - 1 + orderedFocusable.length) % orderedFocusable.length
            : (activeIndex + 1) % orderedFocusable.length;
        orderedFocusable[nextIndex]?.focus();
    }

    private _onKeyDown = (e: KeyboardEvent): void => {
        this._handleContentTabLoop(e);
        this.dispatchEvent(
            new CustomEvent('overlay-keydown', {
                detail: { key: e.key, originalEvent: e },
            })
        );
    };

    protected render(): TemplateResult {
        const contentClass = `der-tm-roundtrip-gallery-overlay__content${
            this.fullscreenMode ? ' der-tm-roundtrip-gallery-overlay__content--fullscreen' : ''
        }`;
        const contentInnerClass = `der-tm-roundtrip-gallery-overlay__content-inner${
            this.fullscreenMode ? ' der-tm-roundtrip-gallery-overlay__content-inner--fullscreen' : ''
        }`;

        return html`
            <dialog
                class="der-tm-roundtrip-gallery-overlay"
                tabindex="-1"
                @cancel=${this._onCancel}
                @keydown=${this._onKeyDown}
                @pointerdown=${this._onPointerDown}
                aria-label=${this.ariaLabel}
            >
                <div class=${contentClass}>
                    <div class=${contentInnerClass}>${this.content}</div>
                </div>
                <div class="der-tm-roundtrip-gallery-overlay__header">
                    ${
                        this.backEnabled
                            ? html`
                                  <button
                                      class="der-tm-roundtrip-gallery-overlay__back"
                                      @click=${this._back}
                                      aria-label=${msg('Galerie', {
                                          desc: 'Back button label in an overlay dialog',
                                      })}
                                  >
                                      ${systemChevronLeft({ width: '40px', height: '40px' })}
                                      <span class="der-tm-roundtrip-gallery-overlay__back-text">
                                          ${msg('Galerie', { desc: 'Back button label in an overlay dialog' })}
                                      </span>
                                  </button>
                              `
                            : nothing
                    }
                    <button
                        class="der-tm-roundtrip-gallery-overlay__close"
                        @click=${this._close}
                        aria-label=${msg('Close overlay', {
                            desc: 'Close button label in an overlay dialog',
                        })}
                    >
                        <span class="der-tm-roundtrip-gallery-overlay__close-text">
                            ${msg('Schliessen', { desc: 'Close button text in an overlay dialog' })}
                        </span>
                        ${systemCross({ width: '40px', height: '40px' })}
                    </button>
                </div>
            </dialog>
        `;
    }
}
